import React, { useMemo, useEffect } from 'react';
import { MultiLineChart } from '@/components/ui/chart-wrapper';
import DataTable from './DataTable';
import WidgetBase from './WidgetBase';
import { useWidgetData } from '../../hooks/useWidgetData';
import { exportChartDataToCSV } from '../../utils/exportToCSV';
import { useChartColors } from '../../hooks/useChartColors';

/**
 * Multi-Line Widget Component
 * Modernized replacement for MultiLineList.js
 * Displays multiple line datasets for comparison
 */
function MultiLineWidget({ data, onLegendUpdate, onExportReady, onDataReady, mode = 'chart' }) {
    const colors = useChartColors();

    // Fetch data using custom hook
    const { data: apiData, loading, error } = useWidgetData(
        data.url,
        data.RequestJson,
        data.RequestJson1
    );

    // Transform API data to Chart.js format
    // API returns lineDataSet[0].datasets[i]["x/y"] for series 1
    // and lineDataSet2[0].datasets[i]["y"] for series 2
    const chartData = useMemo(() => {
        if (!apiData || apiData.length === 0) return null;

        try {
            const lineSet1 = apiData[0].lineDataSet?.[0];
            const lineSet2 = apiData[0].lineDataSet2?.[0];

            if (!lineSet1 || !lineSet1.datasets || lineSet1.datasets.length === 0) return null;

            // Build x-axis labels from "x" property of each item in lineDataSet[0].datasets
            // Last item is often a summary/total row — skip it (same as dev-v3 uses length-1)
            const itemCount = lineSet1.datasets.length - 1;
            const labels = [];
            const series1Data = [];
            for (let i = 0; i < itemCount; i++) {
                labels.push(lineSet1.datasets[i]["x"]);
                series1Data.push(lineSet1.datasets[i]["y"]);
            }

            // Series 1 uses chart1 token, Series 2 uses the widget's ColorCode (matches old MultiLineList.js)
            const color1 = colors.chart1;
            const color2 = data.ColorCode || colors.chart2;

            const datasets = [
                {
                    label: lineSet1.toplabel || 'New',
                    data: series1Data,
                    borderColor: color1,
                    backgroundColor: colors.background,
                    pointBackgroundColor: colors.background,
                    pointBorderColor: color1,
                    pointBorderWidth: 2,
                    tension: 0.4,
                    fill: false,
                    hoverBackgroundColor: colors.chart2
                }
            ];

            // Add second series if available
            if (lineSet2 && lineSet2.datasets && lineSet2.datasets.length > 0) {
                const series2Data = [];
                const count2 = lineSet2.datasets.length - 1;
                for (let i = 0; i < count2; i++) {
                    series2Data.push(lineSet2.datasets[i]["y"]);
                }
                datasets.push({
                    label: lineSet2.toplabel || 'Repeat',
                    data: series2Data,
                    borderColor: color2,
                    backgroundColor: color2,
                    pointBackgroundColor: colors.background,
                    pointBorderColor: color2,
                    pointBorderWidth: 2,
                    tension: 0.4,
                    fill: false,
                    hoverBackgroundColor: colors.chart2
                });
            }

            return { labels, datasets };
        } catch (err) {
            console.error('Error transforming multi-line chart data:', err);
            return null;
        }
    }, [apiData, data, colors]);

    // Table data — uses listDataSet from API response
    // listDataSet: array of {datasets: [{label, data}]} rows
    const { tableData, columns } = useMemo(() => {
        if (!apiData || apiData.length === 0) return { tableData: [], columns: [] };

        try {
            const listDataSet = apiData[0].listDataSet;
            if (!listDataSet || listDataSet.length === 0) return { tableData: [], columns: [] };

            // First row defines column headers (header text is in .data field)
            const cols = (listDataSet[0].datasets || []).map((ds, idx) => ({
                key: `col${idx}`,
                label: ds.data || `Col ${idx + 1}`,
                sortable: true,
                format: (val) => val ?? '-'
            }));

            // Data rows start at index 1 (index 0 is the header row)
            const rows = listDataSet.slice(1).map(row => {
                const obj = {};
                (row.datasets || []).forEach((ds, idx) => {
                    obj[`col${idx}`] = ds.data;
                });
                return obj;
            });

            return { tableData: rows, columns: cols };
        } catch (err) {
            console.error('Error building multi-line table data:', err);
            return { tableData: [], columns: [] };
        }
    }, [apiData]);

    // Extract legend values and sum from API response (matches old MultiLineList.js)
    const legendInfo = useMemo(() => {
        if (!apiData || apiData.length === 0) return null;
        try {
            return {
                text1: apiData[0].legend?.[0]?.legendText || '',
                value1: apiData[0].legend?.[0]?.legendValue || '',
                text2: apiData[0].legend?.[1]?.legendText || '',
                value2: apiData[0].legend?.[1]?.legendValue || '',
                sum: apiData[0].sumOfLegendValues?.[0]?.sumOfLegendValue || ''
            };
        } catch { return null; }
    }, [apiData]);

    // Notify parent whether this widget has data (used to disable toggle / export buttons).
    // Use derived chartData/tableData — raw apiData can be non-empty with no real content.
    useEffect(() => {
        if (loading) return;
        if (onDataReady) onDataReady(chartData !== null || tableData.length > 0);
    }, [loading, chartData, tableData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Report legend (sumOfLegendValue) to parent for CardTitle display
    useEffect(() => {
        if (!apiData || apiData.length === 0 || !onLegendUpdate) return;
        try {
            const sumVal = apiData[0].sumOfLegendValues?.[0]?.sumOfLegendValue ?? '';
            if (sumVal) onLegendUpdate(data.id, String(sumVal));
        } catch (e) { /* ignore */ }
    }, [apiData]);

    // Export handler
    const handleExport = () => {
        if (chartData) {
            exportChartDataToCSV(chartData, data.title || 'multi-line-chart-data', {
                xLabel: 'Date',
                datasetLabels: ['New Claims', 'Repeat Claims']
            });
        }
    };

    // Register export handler with parent (for header-level export button)
    useEffect(() => {
        if (onExportReady) onExportReady(handleExport);
    }, [chartData]);

    const color1 = colors.chart1;
    const color2 = data.ColorCode || colors.chart2;

    const chartOptions = {
        scales: {
            x: {
                display: false,
                offset: true,
                ticks: { maxTicksLimit: 12 }
            },
            y: {
                title: {
                    display: !!(data.YlabelString),
                    text: data.YlabelString || ''
                },
                beginAtZero: true,
                border: {
                    display: false
                }
            }
        },
        plugins: {
            legend: { display: false }
        }
    };

    return (
        <WidgetBase
            loading={loading}
            error={error}
            chartComponent={
                chartData ? (
                    <div>
                        {/* Legend rows (matches old "transitions" section) */}
                        {legendInfo && (legendInfo.text1 || legendInfo.text2) && (
                            <div className="flex justify-center gap-6 mb-3">
                                {legendInfo.text1 && (
                                    <div className="flex items-center gap-2">
                                        <span className="inline-block w-3 h-3 rounded-sm" style={{ background: color1 }} />
                                        <span className="font-semibold text-sm">{legendInfo.value1}</span>
                                        <span className="text-sm text-muted-foreground">{legendInfo.text1}</span>
                                    </div>
                                )}
                                {legendInfo.text2 && (
                                    <div className="flex items-center gap-2">
                                        <span className="inline-block w-3 h-3 rounded-sm" style={{ background: color2 }} />
                                        <span className="font-semibold text-sm">{legendInfo.value2}</span>
                                        <span className="text-sm text-muted-foreground">{legendInfo.text2}</span>
                                    </div>
                                )}
                            </div>
                        )}
                        <MultiLineChart
                            datasets={chartData.datasets}
                            labels={chartData.labels}
                            height={280}
                            options={chartOptions}
                        />
                    </div>
                ) : null
            }
            tableComponent={
                tableData.length > 0 ? (
                    <DataTable
                        columns={columns}
                        data={tableData}
                        pagination={{ enabled: true, itemsPerPage: 6 }}
                        sortable={true}
                        searchable={false}
                    />
                ) : (
                    <div className="flex items-center justify-center py-12">
                        <p className="text-primary/70">No Data Available</p>
                    </div>
                )
            }
            mode={mode}
        />
    );
}

export default MultiLineWidget;
